import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import { useAppVersion } from '@renderer/hooks/useAppVersion.ts'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'

// Changelogs are served by the builds API (builds.api.hlai.dev). Reads are public and
// unauthenticated, so we fetch directly without a bearer token.
const BUILDS_API_BASE_URL = 'https://builds.api.hlai.dev'
// Newest published entries shown in the "all changelogs" modal view.
const CHANGELOGS_PAGE_SIZE = 20

export type ChangelogEntry = {
  body?: string
  created_at: string
  published_at?: string | null
  id: number
  published?: boolean
  versions?: Array<string>
  title?: string | null
  one_liner?: string | null
}

type ChangelogListResponse = {
  data?: Array<ChangelogEntry>
}

/**
 * Fetch the latest published changelog targeting a specific client version, or `undefined` if
 * there isn't one. Reads are public on the builds API, so no auth is needed.
 */
export async function fetchLatestPublishedChangelog(version: string): Promise<ChangelogEntry | undefined> {
  const params = new URLSearchParams({ onlyPublished: 'true', version, limit: '1' })
  const resp = await fetch(`${BUILDS_API_BASE_URL}/api/changelogs?${params.toString()}`)
  if (!resp.ok) {
    console.warn('Error fetching changelog for version', version)
    return undefined
  }
  const json: ChangelogListResponse = await resp.json()
  return json.data?.[0]
}

const latestChangelogAtom = atom<ChangelogEntry>()
const changelogsAtom = atom<Array<ChangelogEntry>>()
const changelogsViewedAtom = atomWithGlobalStore<Array<number>>('general', 'changelogs.viewed', [])
const installedAtVersionAtom = atomWithGlobalStore<string | undefined>('general', 'installedAtVersion', undefined)

export const useLatestChangelog = () => {
  const [latestChangelog, setLatestChangelog] = useAtom(latestChangelogAtom)
  const { version } = useAppVersion()

  useEffect(() => {
    if (!version) {
      return
    }
    const load = async () => {
      const latest = await fetchLatestPublishedChangelog(version)
      if (latest) {
        setLatestChangelog(latest)
      }
    }
    load()
  }, [version, setLatestChangelog])

  return latestChangelog
}

export const useChangelogs = () => {
  const [changelogs, setChangelogs] = useAtom(changelogsAtom)

  useEffect(() => {
    const load = async () => {
      const params = new URLSearchParams({ onlyPublished: 'true', limit: String(CHANGELOGS_PAGE_SIZE) })
      const resp = await fetch(`${BUILDS_API_BASE_URL}/api/changelogs?${params.toString()}`)
      if (!resp.ok) {
        console.warn('Error fetching changelogs')
        return
      }
      const json: ChangelogListResponse = await resp.json()
      if (json.data) {
        setChangelogs(json.data)
      }
    }
    load()
  }, [setChangelogs])

  return changelogs
}

export const useHasNewChangelog = () => {
  const latestChangelog = useLatestChangelog()
  const changelogsViewed = useAtomValue(changelogsViewedAtom)
  const installedAtVersion = useAtomValue(installedAtVersionAtom)

  if (!latestChangelog || changelogsViewed.includes(latestChangelog.id)) {
    return false
  }

  // Don't flag a changelog as new if the user freshly installed on a version it targets —
  // that content shipped with their install, so it isn't new to them.
  if (installedAtVersion && latestChangelog.versions?.includes(installedAtVersion)) {
    return false
  }

  return true
}

export const useSetChangelogViewed = () => {
  const setChangelogsViewed = useSetAtom(changelogsViewedAtom)
  return useCallback(
    (changelogId: number) => {
      setChangelogsViewed((prev) => {
        if (prev.includes(changelogId)) {
          return prev
        }
        return [...prev, changelogId]
      })
    },
    [setChangelogsViewed],
  )
}
